import { useEffect, useState } from 'react';
import { fetchImage } from '../lib/api';

/**
 * A stored signature, fetched with the session token and shown from an object
 * URL.
 *
 * `<img src="/api/advances/…/signature">` cannot work: the browser does not put
 * an Authorization header on an image request, so it would come back 401. The
 * alternative — a signed URL in a query string — would put a credential in
 * something that gets logged and shared, so: fetch, blob, object URL, revoke.
 *
 * `onReady` lets a printed document wait for the ink before calling
 * `window.print()`; a receipt that prints without the signature on it is worse
 * than no receipt.
 */

interface Props {
  /** An advance's signature… */
  advanceId?: string;
  /** …or any other stored signature by its API path, e.g. `/loans/<id>/signature`. */
  path?: string;
  alt?: string;
  onReady?: () => void;
}

export default function SignatureImage({ advanceId, path, alt = 'signature', onReady }: Props) {
  const source = path ?? `/advances/${advanceId}/signature`;
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    let objectUrl: string | null = null;

    // Through the API base, with the token: a bare `/api/…` here worked only
    // under the dev proxy and came back as HTML from pages.dev in production.
    fetchImage(source)
      .then((blob) => {
        if (!live) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (live) {
          setFailed(true);
          onReady?.();
        }
      });

    return () => {
      live = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [source, onReady]);

  if (failed) {
    return <div className="sub">The signature image could not be loaded.</div>;
  }
  if (!url) {
    return <div className="sub">Loading the signature…</div>;
  }
  return <img src={url} alt={alt} onLoad={onReady} style={{ width: '100%', display: 'block' }} />;
}
